import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import PdfViewer from '../../components/PdfViewer';
import {
  Package, Truck, Search, CheckCircle, Printer, Camera,
  Loader2, Box, MapPin, Calendar, ClipboardList, Gift,
  AlertTriangle, FileText, Plus, X, Upload, ArrowRight
} from 'lucide-react';
import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API_BASE, headers: { 'Content-Type': 'application/json' } });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('at1s_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const deliveryAPI = {
  getReadyOrders: async () => (await api.get('/delivery/orders/ready')).data,
  getPickOrders: async () => (await api.get('/delivery/orders')).data,
  getPickedOrders: async () => (await api.get('/delivery/picked')).data,
  getShippedOrders: async () => (await api.get('/delivery/shipped')).data,
  getPendingParts: async () => (await api.get('/delivery/pending-parts')).data,
  createDelivery: async (orderId, photo, notes) => {
    const formData = new FormData();
    if (photo) formData.append('photo', photo);
    formData.append('notes', notes || '');
    return (await api.post(`/delivery/orders/${orderId}/create-delivery`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data;
  },
  pickAndPack: async (orderId, data) => (await api.post(`/delivery/orders/${orderId}/pick`, data)).data,
  markInTransit: async (orderId, shipmentId) => {
    const formData = new FormData();
    if (shipmentId) formData.append('shipment_id', shipmentId);
    return (await api.post(`/delivery/orders/${orderId}/mark-in-transit`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data;
  },
  markDelivered: async (orderId, photo, notes, shipmentId, freebies) => {
    const formData = new FormData();
    formData.append('photo', photo);
    formData.append('notes', notes);
    if (shipmentId) formData.append('shipment_id', shipmentId);
    if (freebies && freebies.length > 0) formData.append('freebies', JSON.stringify(freebies));
    return (await api.post(`/delivery/orders/${orderId}/deliver`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })).data;
  },
  getPackingList: async (orderId) => {
    const res = await api.get(`/delivery/orders/${orderId}/packing-list`, { responseType: 'blob' });
    const blob = new Blob([res.data], { type: 'application/pdf' });
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  },
};

const formatAT1S = (item) => `AT1SM${item.merv}-${item.size.toUpperCase().replace(/ /g, '')}`;

const DeliveryDashboard = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [tab, setTab] = useState('create');
  const [readyOrders, setReadyOrders] = useState([]);
  const [pickOrders, setPickOrders] = useState([]);
  const [pickedOrders, setPickedOrders] = useState([]);
  const [shippedOrders, setShippedOrders] = useState([]);
  const [pendingParts, setPendingParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Create Delivery state
  const [creatingOrder, setCreatingOrder] = useState(null);
  const [createPhoto, setCreatePhoto] = useState(null);
  const [createPhotoPreview, setCreatePhotoPreview] = useState(null);
  const [createNotes, setCreateNotes] = useState('');
  const [createLoading, setCreateLoading] = useState(false);

  // Picking state
  const [pickingOrder, setPickingOrder] = useState(null);
  const [selectedItems, setSelectedItems] = useState({});
  const [shippedQty, setShippedQty] = useState({});
  const [pickLoading, setPickLoading] = useState(false);

  // Packing list state
  const [packingPdfUrl, setPackingPdfUrl] = useState(null);
  const [packingOrderId, setPackingOrderId] = useState(null);

  // Delivery state
  const [deliveringOrder, setDeliveringOrder] = useState(null);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [deliveryPhoto, setDeliveryPhoto] = useState(null);
  const [deliveryPhotoPreview, setDeliveryPhotoPreview] = useState(null);
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [deliverLoading, setDeliverLoading] = useState(false);
  const [freebies, setFreebies] = useState([]);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const [ready, pick, picked, shipped, pending] = await Promise.all([
        deliveryAPI.getReadyOrders(),
        deliveryAPI.getPickOrders(),
        deliveryAPI.getPickedOrders(),
        deliveryAPI.getShippedOrders(),
        deliveryAPI.getPendingParts(),
      ]);
      setReadyOrders(ready);
      setPickOrders(pick);
      setPickedOrders(picked);
      setShippedOrders(shipped);
      setPendingParts(pending);
    } catch {
      toast({ title: 'Error', description: 'Failed to load orders', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // ── Create Delivery handlers ──────────────────────────────────────
  const openCreateDelivery = (order) => {
    setCreatingOrder(order);
    setCreatePhoto(null);
    setCreatePhotoPreview(null);
    setCreateNotes('');
  };

  const handleCreateDelivery = async () => {
    setCreateLoading(true);
    try {
      await deliveryAPI.createDelivery(creatingOrder.id, createPhoto, createNotes);
      toast({ title: 'Delivery Created', description: `PGI document created for ${creatingOrder.id}. Order moved to processing.` });
      setCreatingOrder(null);
      fetchOrders();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setCreateLoading(false); }
  };

  // ── Picking handlers ──────────────────────────────────────────────
  const openPickingScreen = (order) => {
    setPickingOrder(order);
    const isPartial = order.status === 'partially_delivered';
    const items = isPartial && order.remaining_items?.length > 0 ? order.remaining_items : order.items;
    const sel = {};
    const qty = {};
    items.forEach((item, idx) => { sel[idx] = false; qty[idx] = item.quantity; });
    setSelectedItems(sel);
    setShippedQty(qty);
  };

  const getPickableItems = () => {
    if (!pickingOrder) return [];
    const isPartial = pickingOrder.status === 'partially_delivered';
    return isPartial && pickingOrder.remaining_items?.length > 0 ? pickingOrder.remaining_items : pickingOrder.items;
  };

  const toggleSelectAll = (checked) => {
    const sel = {};
    Object.keys(selectedItems).forEach(k => { sel[k] = checked; });
    setSelectedItems(sel);
  };

  const allSelected = Object.values(selectedItems).length > 0 && Object.values(selectedItems).every(v => v);
  const anySelected = Object.values(selectedItems).some(v => v);

  const handlePickAndPack = async () => {
    if (!anySelected) { toast({ title: 'Select Items', description: 'Please select at least one item.', variant: 'destructive' }); return; }
    setPickLoading(true);
    try {
      const pickableItems = getPickableItems();
      const items = [];
      pickableItems.forEach((item, idx) => {
        if (selectedItems[idx]) items.push({ size: item.size, merv: item.merv, quantity: shippedQty[idx] || item.quantity });
      });
      const result = await deliveryAPI.pickAndPack(pickingOrder.id, { items, warehouse_id: '' });
      toast({ title: 'Picked & Packed', description: result.message });
      setPickingOrder(null);
      fetchOrders();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setPickLoading(false); }
  };

  // ── In-Transit handler ────────────────────────────────────────────
  const handleMarkInTransit = async (orderId, shipmentId) => {
    try {
      const result = await deliveryAPI.markInTransit(orderId, shipmentId);
      toast({ title: 'In Transit', description: result.message });
      fetchOrders();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const handlePrintPackingList = async (orderId) => {
    try {
      const dataUrl = await deliveryAPI.getPackingList(orderId);
      setPackingPdfUrl(dataUrl);
      setPackingOrderId(orderId);
    } catch { toast({ title: 'Error', description: 'Failed to generate packing list', variant: 'destructive' }); }
  };

  // ── Delivery handlers ─────────────────────────────────────────────
  const openDeliveryScreen = (order, shipment = null) => {
    setDeliveringOrder(order);
    setSelectedShipment(shipment);
    setDeliveryPhoto(null);
    setDeliveryPhotoPreview(null);
    setDeliveryNotes('');
    setFreebies([]);
  };

  const addFreebie = () => setFreebies([...freebies, { description: '', quantity: 1 }]);
  const removeFreebie = (idx) => setFreebies(freebies.filter((_, i) => i !== idx));
  const updateFreebie = (idx, field, value) => {
    const updated = [...freebies];
    updated[idx] = { ...updated[idx], [field]: value };
    setFreebies(updated);
  };

  const handleMarkDelivered = async () => {
    if (!deliveryPhoto) { toast({ title: 'Photo Required', description: 'Please attach a proof of delivery photo.', variant: 'destructive' }); return; }
    setDeliverLoading(true);
    try {
      const validFreebies = freebies.filter(f => f.description.trim());
      const result = await deliveryAPI.markDelivered(deliveringOrder.id, deliveryPhoto, deliveryNotes, selectedShipment?.shipment_id || '', validFreebies);
      toast({ title: 'Delivered', description: result.message });
      setDeliveringOrder(null);
      fetchOrders();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setDeliverLoading(false); }
  };

  // ── Filtering ─────────────────────────────────────────────────────
  const getFilteredOrders = () => {
    const filterFn = (o) =>
      (o.id || o.order_id || '').toLowerCase().includes(search.toLowerCase()) ||
      (o.customer_name || '').toLowerCase().includes(search.toLowerCase());
    if (tab === 'create') return readyOrders.filter(filterFn);
    if (tab === 'pick') return pickOrders.filter(filterFn);
    if (tab === 'transit') return pickedOrders.filter(filterFn);
    if (tab === 'deliver') return shippedOrders.filter(filterFn);
    return pendingParts.filter(filterFn);
  };
  const filtered = getFilteredOrders();

  const statusBadge = (status) => ({
    confirmed: 'bg-blue-100 text-blue-800',
    processing: 'bg-yellow-100 text-yellow-800',
    partially_delivered: 'bg-orange-100 text-orange-800',
    shipped: 'bg-purple-100 text-purple-800',
    delivered: 'bg-green-100 text-green-800',
    picked: 'bg-cyan-100 text-cyan-800',
    in_transit: 'bg-indigo-100 text-indigo-800',
  }[status] || 'bg-gray-100 text-gray-800');

  return (
    <Layout title="Delivery Portal" role="delivery">
      <div className="space-y-6 p-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800" data-testid="delivery-title">Delivery Portal</h1>
            <p className="text-slate-500 text-sm">Welcome, {user?.name}</p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchOrders} disabled={loading} data-testid="refresh-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 flex-wrap">
          {[
            { key: 'create', icon: FileText, label: 'Create Delivery', count: readyOrders.length },
            { key: 'pick', icon: ClipboardList, label: 'Pick & Pack', count: pickOrders.length },
            { key: 'transit', icon: Truck, label: 'In Transit', count: pickedOrders.length },
            { key: 'deliver', icon: CheckCircle, label: 'Delivered', count: shippedOrders.length },
            { key: 'pending', icon: Package, label: 'Pending Parts', count: pendingParts.length },
          ].map(t => (
            <Button key={t.key} variant={tab === t.key ? 'default' : 'outline'} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}>
              <t.icon className="h-4 w-4 mr-2" /> {t.label} ({t.count})
            </Button>
          ))}
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Search by order ID or customer..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="delivery-search" />
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
        ) : filtered.length === 0 ? (
          <Card><CardContent className="py-16 text-center"><Box className="h-12 w-12 mx-auto mb-4 text-slate-300" /><p className="text-slate-500">{tab === 'pending' ? 'All parts fulfilled' : 'No orders in this stage'}</p></CardContent></Card>
        ) : tab === 'pending' ? (
          <PendingPartsView items={filtered} />
        ) : tab === 'create' ? (
          <div className="grid gap-4">{filtered.map(o => (
            <OrderCard key={o.id} order={o} statusBadge={statusBadge} actions={
              <Button size="sm" onClick={() => openCreateDelivery(o)} data-testid={`create-del-btn-${o.id}`}>
                <FileText className="h-4 w-4 mr-1" /> Create Delivery
              </Button>
            } />
          ))}</div>
        ) : tab === 'pick' ? (
          <div className="grid gap-4">{filtered.map(o => (
            <OrderCard key={o.id} order={o} statusBadge={statusBadge} showRemaining actions={
              <Button size="sm" onClick={() => openPickingScreen(o)} data-testid={`pick-btn-${o.id}`}>
                <ClipboardList className="h-4 w-4 mr-1" /> Pick & Pack
              </Button>
            } />
          ))}</div>
        ) : tab === 'transit' ? (
          <div className="grid gap-4">{filtered.map(o => (
            <OrderCard key={o.id} order={o} statusBadge={statusBadge} actions={
              <div className="flex flex-col gap-2">
                {(o.shipments || []).filter(sh => sh.status === 'picked').map(sh => (
                  <Button key={sh.shipment_id} size="sm" onClick={() => handleMarkInTransit(o.id, sh.shipment_id)} data-testid={`transit-btn-${sh.shipment_id}`}>
                    <ArrowRight className="h-4 w-4 mr-1" /> Ship {sh.shipment_id.slice(-4)}
                  </Button>
                ))}
                <Button variant="outline" size="sm" onClick={() => handlePrintPackingList(o.id)} data-testid={`packing-btn-${o.id}`}>
                  <Printer className="h-4 w-4 mr-1" /> Packing List
                </Button>
              </div>
            } shipmentInfo={(o.shipments || []).filter(sh => sh.status === 'picked')} />
          ))}</div>
        ) : tab === 'deliver' ? (
          <div className="grid gap-4">{filtered.map(o => {
            const undelivered = (o.shipments || []).filter(sh => sh.status === 'in_transit' || sh.status === 'shipped');
            return (
              <OrderCard key={o.id} order={o} statusBadge={statusBadge} actions={
                <div className="flex flex-col gap-2">
                  {undelivered.length > 1 ? undelivered.map(sh => (
                    <Button key={sh.shipment_id} size="sm" onClick={() => openDeliveryScreen(o, sh)} data-testid={`deliver-btn-${o.id}-${sh.shipment_id}`}>
                      <Camera className="h-4 w-4 mr-1" /> Deliver {sh.shipment_id.slice(-4)}
                    </Button>
                  )) : (
                    <Button size="sm" onClick={() => openDeliveryScreen(o, undelivered[0] || null)} data-testid={`deliver-btn-${o.id}`}>
                      <Camera className="h-4 w-4 mr-1" /> Mark Delivered
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => handlePrintPackingList(o.id)} data-testid={`packing-btn-${o.id}`}>
                    <Printer className="h-4 w-4 mr-1" /> Packing List
                  </Button>
                </div>
              } shipmentInfo={undelivered} />
            );
          })}</div>
        ) : null}
      </div>

      {/* ── Create Delivery Dialog ─────────────────────────────────── */}
      <Dialog open={!!creatingOrder} onOpenChange={() => setCreatingOrder(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-blue-600" /> Create Delivery — {creatingOrder?.id}</DialogTitle>
            <DialogDescription>Create a delivery document (Post Goods Issue). Optional: attach an image of the order items.</DialogDescription>
          </DialogHeader>
          {creatingOrder && (
            <div className="space-y-4">
              <div className="text-sm text-slate-600">
                <p><strong>Customer:</strong> {creatingOrder.customer_name}</p>
                <p><strong>Ship To:</strong> {creatingOrder.shipping_address || 'N/A'}</p>
                <p className="mt-2"><strong>Items:</strong></p>
                {creatingOrder.items.map((item, idx) => (
                  <p key={`${item.size}-${item.merv}-${idx}`} className="text-xs text-slate-500 ml-2">{formatAT1S(item)} &times; {item.quantity}</p>
                ))}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Order Image (optional)</label>
                {createPhotoPreview && (
                  <div className="border rounded-lg p-2 text-center">
                    <img src={createPhotoPreview} alt="Order" className="max-h-48 mx-auto rounded" />
                  </div>
                )}
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => document.getElementById('create-delivery-camera').click()} data-testid="create-delivery-camera-btn">
                    <Camera className="h-4 w-4 mr-2" /> Take Photo
                  </Button>
                  <Button variant="outline" className="flex-1" onClick={() => document.getElementById('create-delivery-gallery').click()} data-testid="create-delivery-gallery-btn">
                    <Upload className="h-4 w-4 mr-2" /> From Gallery
                  </Button>
                </div>
                <input id="create-delivery-camera" type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={e => { const f = e.target.files[0]; if (f) { setCreatePhoto(f); setCreatePhotoPreview(URL.createObjectURL(f)); } }} />
                <input id="create-delivery-gallery" type="file" accept="image/*" className="hidden" data-testid="create-delivery-photo-input"
                  onChange={e => { const f = e.target.files[0]; if (f) { setCreatePhoto(f); setCreatePhotoPreview(URL.createObjectURL(f)); } }} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Notes</label>
                <Input value={createNotes} onChange={e => setCreateNotes(e.target.value)} placeholder="Any notes for this delivery..." data-testid="create-delivery-notes" />
              </div>
              <Button onClick={handleCreateDelivery} disabled={createLoading} className="w-full" data-testid="confirm-create-delivery-btn">
                {createLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
                Create Delivery Document
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Picking Dialog ──────────────────────────────────────────── */}
      <Dialog open={!!pickingOrder} onOpenChange={() => setPickingOrder(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ClipboardList className="h-5 w-5 text-blue-600" /> Pick & Pack — {pickingOrder?.id}</DialogTitle>
            <DialogDescription>
              Select items and confirm quantities. Stock will be reduced from inventory.
              {pickingOrder?.status === 'partially_delivered' && <span className="block mt-1 text-orange-600 font-medium">Partial order — remaining items only.</span>}
            </DialogDescription>
          </DialogHeader>
          {pickingOrder && (
            <div className="space-y-4">
              <div className="text-sm text-slate-600">
                <p><strong>Customer:</strong> {pickingOrder.customer_name}</p>
                <p><strong>Ship To:</strong> {pickingOrder.shipping_address || 'N/A'}</p>
              </div>
              {pickingOrder.shipments?.length > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-orange-800 mb-2"><AlertTriangle className="h-4 w-4 inline mr-1" />Previous Shipments ({pickingOrder.shipments.length})</p>
                  {pickingOrder.shipments.map(sh => (
                    <div key={sh.shipment_id} className="text-xs text-orange-700 ml-4">
                      {sh.shipment_id}: {sh.items.map(it => `${formatAT1S(it)} x${it.quantity}`).join(', ')}
                      <Badge className={`ml-2 text-xs ${sh.status === 'delivered' ? 'bg-green-100 text-green-700' : sh.status === 'in_transit' ? 'bg-indigo-100 text-indigo-700' : 'bg-cyan-100 text-cyan-700'}`}>{sh.status.replace('_', ' ')}</Badge>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2 py-2 border-b">
                <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} data-testid="select-all" />
                <span className="text-sm font-medium">Select All</span>
              </div>
              <div className="space-y-3">
                {getPickableItems().map((item, idx) => (
                  <div key={`pick-${item.size}-${item.merv}-${idx}`} className={`flex items-center gap-4 p-3 rounded-lg border ${selectedItems[idx] ? 'bg-blue-50 border-blue-200' : 'bg-white border-slate-200'}`} data-testid={`pick-item-${idx}`}>
                    <Checkbox checked={selectedItems[idx] || false} onCheckedChange={checked => setSelectedItems(prev => ({ ...prev, [idx]: checked }))} data-testid={`pick-checkbox-${idx}`} />
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">{formatAT1S(item)}</p>
                      <p className="text-xs text-slate-500">{item.size} &middot; MERV {item.merv}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">Qty:</span>
                      <Input type="number" min={1} max={item.quantity} value={shippedQty[idx] ?? item.quantity}
                        onChange={e => setShippedQty(prev => ({ ...prev, [idx]: parseInt(e.target.value) || 1 }))}
                        className="w-20 h-8 text-center" data-testid={`pick-qty-${idx}`} />
                      <span className="text-xs text-slate-400">/ {item.quantity}</span>
                    </div>
                    <Badge variant={selectedItems[idx] ? 'default' : 'secondary'} className="text-xs">{selectedItems[idx] ? 'Picked' : 'Pending'}</Badge>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 pt-4 border-t">
                <Button onClick={handlePickAndPack} disabled={!anySelected || pickLoading} className="flex-1" data-testid="confirm-pick-btn">
                  {pickLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Package className="h-4 w-4 mr-2" />}
                  Pick & Pack
                </Button>
                <Button variant="outline" onClick={() => handlePrintPackingList(pickingOrder.id)} data-testid="print-packing-btn">
                  <Printer className="h-4 w-4 mr-1" /> Packing List
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Delivery Dialog ─────────────────────────────────────────── */}
      <Dialog open={!!deliveringOrder} onOpenChange={() => setDeliveringOrder(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CheckCircle className="h-5 w-5 text-green-600" /> Confirm Delivery — {deliveringOrder?.id}</DialogTitle>
            <DialogDescription>
              Upload proof of delivery photo and confirm.
              {selectedShipment && <span className="block mt-1 text-blue-600 font-medium">Shipment: {selectedShipment.shipment_id}</span>}
            </DialogDescription>
          </DialogHeader>
          {deliveringOrder && (
            <div className="space-y-4">
              <div className="text-sm text-slate-600">
                <p><strong>Customer:</strong> {deliveringOrder.customer_name}</p>
                <p><strong>Address:</strong> {deliveringOrder.shipping_address || 'N/A'}</p>
                <p className="mt-2"><strong>Items in this shipment:</strong></p>
                {(selectedShipment?.items || deliveringOrder.items).map((item, idx) => (
                  <p key={`${item.size}-${item.merv}-${idx}`} className="text-xs text-slate-500 ml-2">{formatAT1S(item)} &times; {item.quantity}</p>
                ))}
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Proof of Delivery Photo *</label>
                {deliveryPhotoPreview && (
                  <div className="border rounded-lg p-2 text-center">
                    <img src={deliveryPhotoPreview} alt="Delivery" className="max-h-48 mx-auto rounded" />
                  </div>
                )}
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => document.getElementById('delivery-camera-input').click()} data-testid="delivery-camera-btn">
                    <Camera className="h-4 w-4 mr-2" /> Take Photo
                  </Button>
                  <Button variant="outline" className="flex-1" onClick={() => document.getElementById('delivery-gallery-input').click()} data-testid="delivery-gallery-btn">
                    <Upload className="h-4 w-4 mr-2" /> From Gallery
                  </Button>
                </div>
                <input id="delivery-camera-input" type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={e => { const f = e.target.files[0]; if (f) { setDeliveryPhoto(f); setDeliveryPhotoPreview(URL.createObjectURL(f)); } }} />
                <input id="delivery-gallery-input" type="file" accept="image/*" className="hidden" data-testid="delivery-photo-input"
                  onChange={e => { const f = e.target.files[0]; if (f) { setDeliveryPhoto(f); setDeliveryPhotoPreview(URL.createObjectURL(f)); } }} />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Notes (optional)</label>
                <Input value={deliveryNotes} onChange={e => setDeliveryNotes(e.target.value)} placeholder="Left at front door..." data-testid="delivery-notes" />
              </div>
              <div className="space-y-2 border-t pt-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium flex items-center gap-1"><Gift className="h-4 w-4 text-amber-500" /> Freebies Given</label>
                  <Button variant="outline" size="sm" onClick={addFreebie} data-testid="add-freebie-btn"><Plus className="h-3 w-3 mr-1" /> Add</Button>
                </div>
                {freebies.length === 0 && <p className="text-xs text-slate-400">No freebies. Click "Add" to log items given.</p>}
                {freebies.map((fb, idx) => (
                  <div key={`freebie-${idx}`} className="flex gap-2 items-center" data-testid={`freebie-row-${idx}`}>
                    <Input placeholder="Item description" value={fb.description} onChange={e => updateFreebie(idx, 'description', e.target.value)} className="flex-1 h-8 text-sm" data-testid={`freebie-desc-${idx}`} />
                    <Input type="number" min={1} value={fb.quantity} onChange={e => updateFreebie(idx, 'quantity', parseInt(e.target.value) || 1)} className="w-16 h-8 text-center text-sm" data-testid={`freebie-qty-${idx}`} />
                    <Button variant="ghost" size="sm" onClick={() => removeFreebie(idx)} className="h-8 w-8 p-0"><X className="h-3 w-3 text-red-500" /></Button>
                  </div>
                ))}
              </div>
              <Button onClick={handleMarkDelivered} disabled={!deliveryPhoto || deliverLoading} className="w-full" data-testid="confirm-deliver-btn">
                {deliverLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                Confirm Delivery
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Packing List PDF Viewer ─────────────────────────────────── */}
      <Dialog open={!!packingPdfUrl} onOpenChange={() => { setPackingPdfUrl(null); setPackingOrderId(null); }}>
        <DialogContent className="max-w-4xl p-0 pb-2">
          <DialogHeader className="px-6 pt-4 pb-2 border-b">
            <DialogTitle className="flex items-center gap-2"><Printer className="h-5 w-5 text-blue-600" /> Packing List — {packingOrderId}</DialogTitle>
            <DialogDescription>Print this packing list and attach to the package.</DialogDescription>
          </DialogHeader>
          {packingPdfUrl && <PdfViewer dataUrl={packingPdfUrl} orderId={packingOrderId} onDownload={() => { const a = document.createElement('a'); a.href = packingPdfUrl; a.download = `packing-list-${packingOrderId}.pdf`; a.click(); }} />}
        </DialogContent>
      </Dialog>
    </Layout>
  );
};


// ── Order Card Component ─────────────────────────────────────────────
const OrderCard = ({ order, statusBadge, actions, showRemaining, shipmentInfo }) => (
  <Card className="hover:shadow-md transition-shadow" data-testid={`order-card-${order.id}`}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div className="space-y-1 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-slate-800">{order.id}</h3>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(order.status)}`}>
              {order.status === 'partially_delivered' ? 'Partial' : order.status.replace('_', ' ')}
            </span>
            {order.delivery_doc && <Badge variant="outline" className="text-xs bg-green-50 text-green-700">PGI</Badge>}
            {order.shipments?.length > 0 && <Badge variant="outline" className="text-xs">{order.shipments.length} shipment{order.shipments.length !== 1 ? 's' : ''}</Badge>}
          </div>
          <p className="text-sm text-slate-600 flex items-center gap-1"><Package className="h-3.5 w-3.5" /> {order.customer_name}</p>
          <p className="text-sm text-slate-500 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {order.shipping_address || 'N/A'}</p>
          <p className="text-xs text-slate-400 flex items-center gap-1"><Calendar className="h-3 w-3" /> {order.order_date ? new Date(order.order_date).toLocaleDateString() : ''}</p>
          <p className="text-sm text-slate-600 mt-1">{order.items.length} item{order.items.length !== 1 ? 's' : ''} &middot; {order.items.reduce((s, i) => s + i.quantity, 0)} units</p>
          {showRemaining && order.status === 'partially_delivered' && order.remaining_items?.length > 0 && (
            <div className="mt-2 bg-orange-50 rounded p-2 text-xs text-orange-700">
              <strong>Remaining:</strong> {order.remaining_items.map((ri, i) => <span key={`${ri.size}-${ri.merv}-${i}`}>{i > 0 && ', '}{formatAT1S(ri)} x{ri.quantity}</span>)}
            </div>
          )}
          {shipmentInfo && shipmentInfo.length > 0 && (
            <div className="mt-2 space-y-1">
              {shipmentInfo.map(sh => (
                <div key={sh.shipment_id} className="bg-cyan-50 rounded p-2 text-xs text-cyan-700 flex items-center justify-between">
                  <span><strong>{sh.shipment_id}:</strong> {sh.items.map((it, i) => <span key={`${it.size}-${it.merv}-${i}`}>{i > 0 && ', '}{formatAT1S(it)} x{it.quantity}</span>)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 ml-4">{actions}</div>
      </div>
    </CardContent>
  </Card>
);


// ── Pending Parts Report ─────────────────────────────────────────────
const PendingPartsView = ({ items }) => {
  const summary = {};
  items.forEach(o => {
    o.items.forEach(item => {
      const key = `${item.size}|${item.merv}`;
      if (!summary[key]) summary[key] = { size: item.size, merv: item.merv, totalQty: 0, orders: [] };
      summary[key].totalQty += item.quantity;
      summary[key].orders.push(o.order_id);
    });
  });
  const summaryList = Object.values(summary).sort((a, b) => b.totalQty - a.totalQty);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><ClipboardList className="h-5 w-5 text-blue-600" /> Picking Summary — {summaryList.length} unique parts, {items.length} orders</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pending-parts-summary-table">
              <thead><tr className="bg-slate-100 text-slate-600 text-left"><th className="px-3 py-2 font-medium">Product ID</th><th className="px-3 py-2 font-medium">Size</th><th className="px-3 py-2 font-medium">MERV</th><th className="px-3 py-2 font-medium text-right">Total Qty Needed</th><th className="px-3 py-2 font-medium text-right">Orders</th></tr></thead>
              <tbody>
                {summaryList.map((item, idx) => (
                  <tr key={`${item.size}-${item.merv}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 font-medium text-slate-800">AT1SM{item.merv}-{item.size.toUpperCase().replace(/ /g, '')}</td>
                    <td className="px-3 py-2 text-slate-600">{item.size}</td>
                    <td className="px-3 py-2 text-slate-600">{item.merv}</td>
                    <td className="px-3 py-2 text-right font-bold text-blue-700">{item.totalQty}</td>
                    <td className="px-3 py-2 text-right text-slate-500">{item.orders.length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Order Details</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pending-parts-orders-table">
              <thead><tr className="bg-slate-100 text-slate-600 text-left"><th className="px-3 py-2 font-medium">Order ID</th><th className="px-3 py-2 font-medium">Customer</th><th className="px-3 py-2 font-medium">Status</th><th className="px-3 py-2 font-medium">Items</th><th className="px-3 py-2 font-medium text-right">Total Units</th></tr></thead>
              <tbody>
                {items.map((o, idx) => (
                  <tr key={o.order_id || idx} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 font-medium text-slate-800">{o.order_id}</td>
                    <td className="px-3 py-2 text-slate-600">{o.customer_name}</td>
                    <td className="px-3 py-2"><Badge variant="outline" className="text-xs">{o.status}</Badge></td>
                    <td className="px-3 py-2 text-xs text-slate-600">{o.items.map((it, i) => <span key={`${it.size}-${it.merv}-${i}`} className="block">AT1SM{it.merv}-{it.size.toUpperCase().replace(/ /g, '')} x{it.quantity}</span>)}</td>
                    <td className="px-3 py-2 text-right font-bold">{o.total_units}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DeliveryDashboard;
